import { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ProjectService from "../../services/ProjectService";
import { useNotification } from "../../context/NotificationContext";

const SUCCESS_MESSAGE =
    "Thank you. We've received your login details and will investigate the issue. We'll notify you by email as soon as we have an update";

const LoginIssueModal = ({ open, setOpen, projectId, initialLogin = "" }) => {
    const showNotification = useNotification();
    const [loginNotes, setLoginNotes] = useState("");
    const [login, setLogin] = useState("");
    const [loginError, setLoginError] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setLogin(initialLogin || "");
        } else {
            setLoginNotes("");
            setLogin("");
            setLoginError("");
            setPassword("");
            setPasswordError("");
            setLoading(false);
        }
    }, [initialLogin, open]);

    const handleClose = () => {
        if (loading) return;
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (!projectId) {
            showNotification("No project selected", "error", "");
            return;
        }

        if (!login.trim()) {
            setLoginError("Test account login is required.");
            return;
        }

        if (!password.trim()) {
            setPasswordError("Test account password is required.");
            return;
        }

        try {
            setLoading(true);
            await ProjectService.submitLoginIssue(projectId, {
                notes: loginNotes,
                login: login.trim(),
                password,
            });
            showNotification(SUCCESS_MESSAGE, "success", "");
            setOpen(false);
        } catch (error) {
            console.error("Error submitting login issue details:", error);
            showNotification(
                error.response?.data?.message ||
                    "Failed to submit login issue details",
                "error",
                ""
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <Box className="loginIssueModal">
                <DialogTitle className="loginIssueModalTitle">
                    <InfoOutlinedIcon
                        className="loginIssueModalTitleIcon"
                        color="secondary"
                    />
                    Treegress couldn&apos;t sign in automatically
                </DialogTitle>
                <DialogContent className="loginIssueModalContent">
                    <Typography variant="body1" mb={2}>
                        Treegress is designed to work across many different SaaS
                        products automatically. Some applications have unique
                        login flows or security requirements that require
                        additional context.
                    </Typography>
                    <Typography variant="body1" mb={3}>
                        Help us understand your login process so we can
                        investigate and improve support for your application.
                    </Typography>
                    <TextField
                        label="Login notes"
                        placeholder="OTP, magic link, SSO, workspace selection, CAPTCHA, VPN, IP allowlist, or anything else we should know."
                        value={loginNotes}
                        onChange={(event) => setLoginNotes(event.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Test account login"
                        value={login}
                        onChange={(event) => {
                            setLogin(event.target.value);
                            if (loginError) setLoginError("");
                        }}
                        fullWidth
                        required
                        error={!!loginError}
                        helperText={loginError || " "}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Test account password"
                        type="password"
                        value={password}
                        onChange={(event) => {
                            setPassword(event.target.value);
                            if (passwordError) setPasswordError("");
                        }}
                        fullWidth
                        required
                        error={!!passwordError}
                        helperText={
                            passwordError ||
                            "Please provide test account credentials we can use to investigate this issue."
                        }
                    />
                </DialogContent>
                <DialogActions className="loginIssueModalActions">
                    <Button
                        className="loginIssueModalButton"
                        onClick={handleClose}
                        color="secondary"
                        variant="outlined"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    {loading ? (
                        <LoadingButton
                            loading
                            className="loginIssueModalButton"
                            variant="contained"
                            color="secondary"
                            loadingIndicator={
                                <CircularProgress color="white" size={16} />
                            }
                        />
                    ) : (
                        <Button
                            className="loginIssueModalButton"
                            onClick={handleSubmit}
                            color="secondary"
                            variant="contained"
                        >
                            Submit
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default LoginIssueModal;
